import React, { createContext, useContext } from 'react';
import { useScreenSize } from '../hooks/useScreenSize';

/**
 * ScreenSizeContext
 *
 * Provides screen size information to the entire component tree.
 * Use this when multiple components need screen size to avoid
 * creating multiple resize listeners.
 *
 * Usage:
 * 1. Wrap your app with <ScreenSizeProvider>
 * 2. Use useScreenSizeContext() in any component
 */
const ScreenSizeContext = createContext(null);

/**
 * ScreenSizeProvider - Wraps the app to provide screen size context
 *
 * @param {React.ReactNode} children - Child components
 */
export function ScreenSizeProvider({ children }) {
  const screenSize = useScreenSize();

  return (
    <ScreenSizeContext.Provider value={screenSize}>
      {children}
    </ScreenSizeContext.Provider>
  );
}

/**
 * useScreenSizeContext - Access screen size from context
 *
 * @returns {Object} screenSize - { width, height, isMobile, isTablet, isDesktop, breakpoint }
 * @throws {Error} If used outside of ScreenSizeProvider
 *
 * Note: For components that need screen size but don't want to cause
 * re-renders in parent components, use the useScreenSize hook directly instead.
 */
export function useScreenSizeContext() {
  const context = useContext(ScreenSizeContext);
  if (!context) {
    throw new Error('useScreenSizeContext must be used within ScreenSizeProvider');
  }
  return context;
}

export default ScreenSizeContext;
